/**
 * Isomorphic JWT region extraction — uses atob() (available in both
 * browser and Node 16+) so this module can be safely imported from
 * client components, server API routes, and edge middleware alike.
 */
function extractFieldsFromAccessToken(
  accessToken: string
): Record<string, unknown> | undefined {
  const parts = accessToken.split('.');
  if (!parts[1]) return undefined;

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64)) as Record<string, unknown>;
}

export function extractRegionFromAccessToken(accessToken: string): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken) as {
      user_properties?: { region?: { v?: unknown } };
    };
    const region = payload?.user_properties?.region?.v;
    return typeof region === 'string' ? region : undefined;
  } catch {
    return undefined;
  }
}

/** Returns the JWT `exp` claim as epoch milliseconds, or undefined. */
export function extractExpFromAccessToken(accessToken: string): number | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken);
    const exp = payload?.exp;
    return typeof exp === 'number' ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

export function extractKindeUserIdFromAccessToken(
  accessToken: string
): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken);
    const kindeUserId = payload?.kinde_user_id;

    return typeof kindeUserId === 'string' && kindeUserId.trim()
      ? kindeUserId
      : undefined;
  } catch {
    return undefined;
  }
}

export function extractKindeUserIdFromAuth0Token(
  accessToken: string
): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken) as {
      user_properties?: { kinde_user_id?: { v?: string } };
    };
    return payload?.user_properties?.kinde_user_id?.v;
  } catch {
    return undefined;
  }
}

export function extractUserRefIdFromAuth0Token(accessToken: string): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken) as {
      user_properties?: { user_ref_id?: { v?: string } };
    };
    return payload?.user_properties?.user_ref_id?.v;
  } catch {
    return undefined;
  }
}

/**
 * Extract the SSO connection id claim from the access token, when present.
 * SSO-authenticated users (enterprise SAML/OIDC connections) carry
 * `user_properties.sso_connection_id` in their JWT — the same claim the
 * backend reads when stamping `kinde_info.sso_connection_id` at user
 * creation (see ml-scribe `service/auth/verifiers/helpers.py`). Returns
 * undefined for non-SSO (email/social) users.
 */
export function extractSsoConnectionIdFromAccessToken(
  accessToken: string
): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken) as {
      user_properties?: { sso_connection_id?: { v?: string } };
    };
    const ssoConnectionId = payload?.user_properties?.sso_connection_id?.v;
    return typeof ssoConnectionId === 'string' && ssoConnectionId.trim()
      ? ssoConnectionId
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Extract the standard top-level `sub` (subject) claim from the access token —
 * the IdP subject identifier. For Auth0-authed users this is the Auth0 id; for
 * Kinde-authed users it is the Kinde sub. Provider-agnostic by design: the FE
 * reads it from the same access token it sends to the BE, and the BE reads the
 * same `sub` from that token, so FE and BE tag `idp_sub` with an identical
 * value for cross-log correlation (DEV-11834).
 */
export function extractIdpSubFromAccessToken(accessToken: string): string | undefined {
  try {
    const payload = extractFieldsFromAccessToken(accessToken) as { sub?: string };
    return typeof payload?.sub === 'string' && payload.sub.trim()
      ? payload.sub
      : undefined;
  } catch {
    return undefined;
  }
}

/** Extract region from a decoded Kinde middleware token. */
export function extractRegionFromKindeToken(
  token: Record<string, unknown>
): string | undefined {
  const props = token?.user_properties as { region?: { v?: string } } | undefined;
  return props?.region?.v;
}
